import { StandardIngredient, LanguageCode, IngredientCategory, IngredientUnit } from '../types/ingredients';

export function downloadCSV(data: any[], filename: string) {
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => headers.map(header => {
      const value = row[header];
      if (typeof value === 'object') {
        return JSON.stringify(value);
      }
      return value;
    }).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export async function parseCSV(file: File): Promise<Omit<StandardIngredient, 'id' | 'createdAt' | 'updatedAt'>[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const lines = text.split('\n').map(line => line.trim()).filter(Boolean);
        const headers = lines[0].toLowerCase().split(',').map(header => header.trim());
        
        const data = lines.slice(1).map(line => {
          const values = line.split(',').map(value => value.trim());
          const row: Record<string, any> = {};
          
          headers.forEach((header, index) => {
            if (header === 'attributes') {
              try {
                row[header] = JSON.parse(values[index]);
              } catch {
                row[header] = {};
              }
            } else {
              row[header] = values[index];
            }
          });
          
          return {
            name: row.name,
            language: row.language as LanguageCode,
            category: row.category as IngredientCategory,
            aliases: row.aliases.split(';').map((a: string) => a.trim()),
            preferredUnit: row.preferredUnit as IngredientUnit,
            picture: row.picture || '',
            attributes: {
              glutenFree: row.attributes.glutenFree === 'true',
              vegan: row.attributes.vegan === 'true',
              vegetarian: row.attributes.vegetarian === 'true',
              calories: row.attributes.calories
                ? Number(String(row.attributes.calories).replace(',', '.'))
                : undefined,
              organic: row.attributes.organic === 'true',
              allergens: row.attributes.allergens.split(';').map((a: string) => a.trim()),
              seasonal: row.attributes.seasonal.split(';').map((a: string) => a.trim())
            }
          };
        });
        
        resolve(data);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

export function getExampleCSV(): Omit<StandardIngredient, 'id' | 'createdAt' | 'updatedAt'>[] {
  return [
    {
      name: 'Tomato',
      language: 'en',
      category: 'produce',
      aliases: ['tomatoes', 'cherry tomato'],
      preferredUnit: 'gram',
      picture: '',
      attributes: {
        glutenFree: true,
        vegan: true,
        vegetarian: true,
        calories: 18,
        organic: false,
        allergens: [],
        seasonal: ['summer', 'fall']
      }
    },
    {
      name: 'Poulet',
      language: 'fr',
      category: 'meat',
      aliases: ['chicken', 'poulet fermier'],
      preferredUnit: 'gram',
      picture: '',
      attributes: {
        glutenFree: true,
        vegan: false,
        vegetarian: false,
        calories: 239,
        organic: false,
        allergens: [],
        seasonal: []
      }
    }
  ];
}